/**
 * Sale countdown hook — powers the "Sale ends in 6h 12m" chip.
 *
 * Given a creator-set `available_to` date (`YYYY-MM-DD`), returns a
 * live-ticking label counting down to the END of that day in UTC (which
 * is exactly when the backend `_win_to` check flips the sale off).
 *
 * The hook is silent (returns `null`) when:
 *   * `availableTo` is empty.
 *   * The deadline is more than 24h away (we only want urgency on the
 *     LAST day per the product spec).
 *   * The deadline has already passed (the parent card handles the
 *     "Sale ended" state).
 *
 * Consumers should still render the static "Available on X" chip when
 * this hook returns `null`, so both live-urgent and calm states coexist.
 */
import { useEffect, useState } from "react";

function _deadlineMs(availableTo) {
  // End of UTC day so the client counter agrees with the server-side
  // `today_iso > available_to` block (server uses UTC date).
  const t = Date.parse(`${availableTo}T23:59:59.999Z`);
  return isNaN(t) ? null : t;
}

export function useSaleCountdown(availableTo) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!availableTo) return undefined;
    // Re-render every 30 seconds — enough to keep minute-level accuracy
    // without spamming the reconciler on every card.
    const iv = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(iv);
  }, [availableTo]);

  if (!availableTo) return null;
  const deadline = _deadlineMs(availableTo);
  if (deadline == null) return null;

  const remaining = deadline - now;
  if (remaining <= 0) return { expired: true, urgent: true, label: "Ending now" };

  // Only surface urgency on the LAST day (≤ 24 hours to go). Older ranges
  // fall back to the static availability chip.
  if (remaining > 24 * 3600 * 1000) return null;

  const totalMin = Math.max(1, Math.floor(remaining / 60_000));
  const totalH = Math.floor(totalMin / 60);
  const mins = totalMin % 60;

  let label;
  if (totalH >= 1) label = `Sale ends in ${totalH}h ${mins}m`;
  else label = `Sale ends in ${totalMin}m`;

  return { expired: false, urgent: totalH < 1, label };
}
